/* import __COLOCATED_TEMPLATE__ from './start-validate-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import dayjs from 'dayjs';

export default class StartValidateInterfaceComponent extends Component
{
	@service store;

	@tracked selectedTimingPoint = null;
	@tracked tableData = [];
	@tracked isLoading = false;
	@tracked medianDiff = null;

	@action
	async timingPointChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.selectedTimingPoint = null;
			this.tableData = [];
			return;
		}

		this.isLoading = true;
		this.selectedTimingPoint = await this.store.findRecord('IntermediateTimingPoint', browserEvent.target.value);
		await this.loadData();
		this.isLoading = false;
	}

	async loadData()
	{
		if(!this.args.event || !this.selectedTimingPoint)
		{
			this.tableData = [];
			return;
		}

		const entries = [...(await this.args.event.get('entries'))];
		const rows = [];

		for(const entry of entries)
		{
			const startRecord = await entry.get('StartTimingRecordId');
			const intermediateRecords = await entry.get('intermediateTimingRecords');

			let assignedStartTime = null;
			let startImpulseTime = null;
			let itpTime = null;
			let diff = null;

			if(startRecord)
			{
				assignedStartTime = startRecord.assignedStartTime;
				const startImpulse = await startRecord.get('startImpulseId');
				if(startImpulse)
				{
					startImpulseTime = startImpulse.time;
				}
			}

			// Find the intermediate record for the selected timing point
			for(const iTR of intermediateRecords)
			{
				const iTP = await iTR.get('IntermediateTimingPointId');
				if(iTP && iTP.id == this.selectedTimingPoint.id)
				{
					const impulse = await iTR.get('impulseId');
					if(impulse)
					{
						itpTime = impulse.time;
					}
					break;
				}
			}

			// Calculate diff if we have both times
			if(startImpulseTime && itpTime)
			{
				diff = dayjs(itpTime).diff(dayjs(startImpulseTime));
			}

			rows.push({
				bib: entry.bib,
				name: `${entry.firstName || ''} ${entry.lastName || ''}`.trim(),
				assignedStartTime,
				startImpulseTime,
				itpTime,
				diff,
				isOutlier: false
			});
		}

		// Sort by assigned start time
		rows.sort((a, b) => {
			if(!a.assignedStartTime && !b.assignedStartTime) return 0;
			if(!a.assignedStartTime) return 1;
			if(!b.assignedStartTime) return -1;
			return dayjs(a.assignedStartTime).diff(dayjs(b.assignedStartTime));
		});

		// Calculate median diff and mark outliers
		const diffs = rows.filter(r => r.diff !== null).map(r => r.diff);
		if(diffs.length > 0)
		{
			diffs.sort((a, b) => a - b);
			const mid = Math.floor(diffs.length / 2);
			this.medianDiff = diffs.length % 2 === 0
				? (diffs[mid - 1] + diffs[mid]) / 2
				: diffs[mid];

			// Calculate MAD (Median Absolute Deviation) for outlier detection
			const deviations = diffs.map(d => Math.abs(d - this.medianDiff));
			deviations.sort((a, b) => a - b);
			const madMid = Math.floor(deviations.length / 2);
			const mad = deviations.length % 2 === 0
				? (deviations[madMid - 1] + deviations[madMid]) / 2
				: deviations[madMid];

			// Mark outliers (more than 3 MADs from median, minimum 2 seconds)
			const threshold = Math.max(mad * 3, 2000);
			for(const row of rows)
			{
				if(row.diff !== null)
				{
					row.isOutlier = Math.abs(row.diff - this.medianDiff) > threshold;
				}
			}
		}

		this.tableData = rows;
	}

	formatTime(time)
	{
		if(!time) return '';
		return dayjs(time).format('HH:mm:ss.SSS');
	}

	formatDiff(diff)
	{
		if(diff === null || diff === undefined) return '';
		const absDiff = Math.abs(diff);
		const sign = diff < 0 ? '-' : '';
		const hours = Math.floor(absDiff / 3600000);
		const minutes = Math.floor((absDiff % 3600000) / 60000);
		const seconds = Math.floor((absDiff % 60000) / 1000);
		const ms = absDiff % 1000;

		if(hours > 0)
		{
			return `${sign}${hours}:${String(minutes).padStart(2,'0')}:${String(seconds).padStart(2,'0')}.${String(ms).padStart(3,'0')}`;
		}
		return `${sign}${minutes}:${String(seconds).padStart(2,'0')}.${String(ms).padStart(3,'0')}`;
	}
}
